/**
 * Laabha — JWT auth helpers.
 *
 * Mirrors the auth-provider in packages/api-client/src/auth-provider.ts but
 * stripped down to what the desktop app actually needs (no Refine dependency).
 *
 * Token storage matches the rest of AxonBOS so a single login in any AxonBOS
 * app authenticates the user across all of them (yesbos, hros, desktop).
 *
 * Storage keys (compatible with @yesbos/api-client):
 *   axon_token            — JWT access token (Bearer)
 *   axon_refresh_token    — refresh token for /auth/refresh
 *   axon_user             — cached user info (JSON)
 *   axon_company_id       — active company id
 */

const TOKEN_KEY = 'axon_token';
const REFRESH_TOKEN_KEY = 'axon_refresh_token';
const USER_KEY = 'axon_user';
const COMPANY_ID_KEY = 'axon_company_id';

export interface AxonUser {
  id: string;
  email: string;
  full_name?: string;
  company_id?: number;
  role?: string;
}

/**
 * Token-bearing response from /auth/login OR /auth/google.
 *
 * The gateway is INCONSISTENT — /auth/login returns `access_token`, but
 * /auth/google returns `token`. We accept either and normalise on the
 * client. Bug verified 2026-05-13.
 */
interface TokenResponseData {
  access_token?: string;
  token?: string; // /auth/google variant
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
  user?: AxonUser;
  company?: { id: number; name: string; plan?: string; [k: string]: unknown };
}

/** Extract the access token regardless of field name. */
function extractAccessToken(data: TokenResponseData): string {
  const t = data.access_token || data.token || '';
  if (!t || typeof t !== 'string') {
    throw new Error('Login succeeded but no access token in response');
  }
  return t;
}

interface Envelope<T> {
  success: boolean;
  data: T | null;
  error: { code: string; message: string } | null;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getCachedUser(): AxonUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AxonUser;
  } catch {
    return null;
  }
}

export function getCompanyId(): number | null {
  const id = localStorage.getItem(COMPANY_ID_KEY);
  return id ? parseInt(id, 10) : null;
}

/** Build the Authorization header from the stored JWT, if any. */
export function authHeader(): Record<string, string> {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Persist tokens + user to localStorage. Refuses to store empty/null tokens. */
export function setAuth(
  tokens: { access_token: string; refresh_token?: string },
  user?: AxonUser,
) {
  if (!tokens.access_token || typeof tokens.access_token !== 'string') {
    // Defensive: never store an empty/undefined token — it would produce
    // `Authorization: Bearer undefined` on every subsequent request → 401 cascade.
    throw new Error('setAuth: refusing to store empty access_token');
  }
  localStorage.setItem(TOKEN_KEY, tokens.access_token);
  if (tokens.refresh_token)
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    if (user.company_id != null)
      localStorage.setItem(COMPANY_ID_KEY, String(user.company_id));
  }
  window.dispatchEvent(new CustomEvent('axondesktop.auth.changed'));
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(COMPANY_ID_KEY);
  window.dispatchEvent(new CustomEvent('axondesktop.auth.changed'));
}

// ── Auth endpoints ────────────────────────────────────────────────────────

export async function login(email: string, password: string): Promise<AxonUser> {
  const resp = await fetch('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const body = (await resp.json()) as Envelope<TokenResponseData>;
  if (!body.success || !body.data) {
    throw new Error(body.error?.message ?? `Login failed (HTTP ${resp.status})`);
  }
  const access_token = extractAccessToken(body.data);
  setAuth({ access_token, refresh_token: body.data.refresh_token }, body.data.user);

  // /auth/login also returns user inline — fall back to fetchMe if not present
  if (body.data.user) return body.data.user;
  const me = await fetchMe();
  setAuth({ access_token, refresh_token: body.data.refresh_token }, me);
  return me;
}

export async function fetchMe(): Promise<AxonUser> {
  const resp = await fetch('/auth/me', { headers: authHeader() });
  const body = (await resp.json()) as Envelope<AxonUser>;
  if (!body.success || !body.data) {
    throw new Error(body.error?.message ?? `Auth check failed (HTTP ${resp.status})`);
  }
  return body.data;
}

export async function logout() {
  clearAuth();
  // No server-side logout — token expiry handles it
}

/**
 * Check whether the stored access_token is a valid, non-expired JWT.
 * Decodes the payload locally — no network call needed.
 * Returns false if there is no token, it is malformed, or it has expired.
 * Uses a 30-second grace window so we refresh slightly before hard expiry.
 */
export function isJwtValid(): boolean {
  const token = getAccessToken();
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const pad = (s: string) => s + '='.repeat((4 - s.length % 4) % 4);
    const payload = JSON.parse(atob(pad(parts[1]).replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    const exp = payload['exp'] as number | undefined;
    if (!exp) return true; // no exp claim → assume valid
    return Date.now() / 1000 < exp - 30;
  } catch {
    return false;
  }
}

/**
 * Try to silently refresh the Zitadel access_token using the stored refresh_token.
 * Returns true if the refresh succeeded and the new tokens are stored.
 * Returns false if there is no refresh_token or the Zitadel call fails.
 *
 * Constants mirror ZITADEL_ISSUER / LAABHA_CLIENT_ID in zitadel-oidc.ts.
 * Kept here to avoid a circular import (zitadel-oidc.ts imports setAuth from here).
 */
export async function refreshZitadelToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return false;
  try {
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: '372805783957602315',
      refresh_token: refreshToken,
    });
    const resp = await fetch('https://dev-auth.axonbos.com/oauth/v2/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!resp.ok) return false;
    const tokens = (await resp.json()) as { access_token?: string; refresh_token?: string };
    if (!tokens.access_token) return false;
    setAuth({ access_token: tokens.access_token, refresh_token: tokens.refresh_token });
    return true;
  } catch {
    return false;
  }
}

// ── Google Sign-In ────────────────────────────────────────────────────────

/**
 * Fetch the Google OAuth client ID configured on the gateway. Returns null if
 * Google Sign-In isn't configured (env var GOOGLE_OAUTH_CLIENT_ID empty).
 */
export async function getGoogleClientId(): Promise<string | null> {
  try {
    const resp = await fetch('/auth/google-client-id', {
      headers: { Accept: 'application/json' },
    });
    const body = (await resp.json()) as Envelope<{ client_id: string; configured: boolean }>;
    if (!body.success || !body.data?.configured) return null;
    return body.data.client_id || null;
  } catch {
    return null;
  }
}

/**
 * Exchange a Google Identity Services credential (ID token) for an AxonBOS JWT.
 * On success, stores the token and returns the user profile.
 */
export async function loginWithGoogle(credential: string): Promise<AxonUser> {
  const resp = await fetch('/auth/google', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential }),
  });
  const body = (await resp.json()) as Envelope<TokenResponseData>;
  if (!body.success || !body.data) {
    throw new Error(body.error?.message ?? `Google sign-in failed (HTTP ${resp.status})`);
  }
  // /auth/google returns data.token; /auth/login returns data.access_token —
  // extractAccessToken handles either.
  const access_token = extractAccessToken(body.data);
  setAuth({ access_token, refresh_token: body.data.refresh_token }, body.data.user);

  // Some implementations include user in the response; otherwise fetch /me.
  const user = body.data.user ?? (await fetchMe());
  setAuth({ access_token, refresh_token: body.data.refresh_token }, user);
  return user;
}
